/*
 * Llamada entrante (llamadas directas, solo si están encendidas: `GET /meet/config` → llamadas).
 * Con sesión mira cada 3 s `GET /meet/llamadas/entrantes`: lo que suena AHORA para mí. Deja de sonar sola cuando
 * la llamada desaparece de esa lista (la contesté en otro aparato, quien llama colgó o pasaron 45 s).
 * «Aceptar» → `POST /meet/llamadas/{c}/contestar` (gana el primer aparato) y se entra; «Rechazar» →
 * `POST /meet/llamadas/{c}/rechazar`. Se monta en Meet (MeetApp) y en el portal del correo (AppShell, `enPortal`):
 * allí aceptar abre Meet en otra pestaña.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { urlEnMeet } from '../lib/dominios';
import { useStore } from '../store/useStore';
import { Avatar } from './Marca';
import Deslizador from './Deslizador';
import { leerPrefs, textoError, useConfigMeet } from './lib';
import './entrante.css';

/** Timbre suave: dos notas cortas, repetidas cada 2,4 s. Si el navegador no deja sonar (sin gesto previo), calla. */
function useTimbre(activo) {
  useEffect(() => {
    if (!activo) return undefined;
    let ctx;
    try { ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return undefined; }
    const nota = (frec, t0, dur) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = frec;
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(0.16, t0 + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
      o.connect(g).connect(ctx.destination);
      o.start(t0); o.stop(t0 + dur + 0.05);
    };
    const sonar = () => {
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const t = ctx.currentTime + 0.05;
      nota(784, t, 0.35); nota(988, t + 0.38, 0.5);
    };
    sonar();
    const iv = setInterval(sonar, 2400);
    return () => { clearInterval(iv); ctx.close().catch(() => {}); };
  }, [activo]);
}

export default function LlamadaEntrante({ enPortal = false }) {
  const user = useStore((s) => s.user);
  const { llamadas: activas } = useConfigMeet();
  const nav = useNavigate();
  const loc = useLocation();
  const [llamada, setLlamada] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const descartadas = useRef(new Set());

  // Teléfono (o pantalla táctil estrecha): pantalla completa con «desliza para contestar»; ordenador: aviso flotante
  const [completa, setCompleta] = useState(() => window.matchMedia('(max-width: 720px), (pointer: coarse) and (max-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 720px), (pointer: coarse) and (max-width: 1024px)');
    const f = () => setCompleta(mq.matches);
    mq.addEventListener('change', f);
    return () => mq.removeEventListener('change', f);
  }, []);
  // Dentro de la app Orvian Mail la llamada entrante la enseña la app (pantalla nativa): aquí no se duplica
  const enApp = (() => { try { return sessionStorage.getItem('zuno_meet_app') === '1'; } catch { return false; } })();

  useEffect(() => {
    if (!user || !activas || enApp) return undefined;
    let vivo = true;
    const mirar = async () => {
      try {
        const { data } = await api.get('/meet/llamadas/entrantes', { skipAuthRedirect: true });
        if (!vivo) return;
        const l = (data?.llamadas || []).find((x) => !descartadas.current.has(x.codigo));
        // Ya estoy dentro de esa sala: no hace falta sonar
        if (l && !enPortal && window.location.pathname.includes(l.codigo)) { descartadas.current.add(l.codigo); return; }
        setLlamada((a) => (a?.codigo === l?.codigo ? a : l || null));
      } catch { /* sin red: se reintenta */ }
    };
    mirar();
    const iv = setInterval(mirar, 3000);
    return () => { vivo = false; clearInterval(iv); };
  }, [user, activas, enPortal, enApp]);

  useTimbre(!!llamada);

  const responder = useCallback(async (aceptar) => {
    const l = llamada;
    if (!l || ocupado) return;
    descartadas.current.add(l.codigo);
    if (!aceptar) {
      setLlamada(null);
      api.post(`/meet/llamadas/${l.codigo}/rechazar`, {}).catch(() => {});
      return;
    }
    // Pestaña nueva ANTES del await: los navegadores solo dejan abrirla dentro del gesto del clic
    const pestana = enPortal ? window.open('about:blank', '_blank') : null;
    setOcupado(true);
    try {
      await api.post(`/meet/llamadas/${l.codigo}/contestar`);
      setLlamada(null);
      if (enPortal) {
        const url = urlEnMeet(`/${l.codigo}?directo=1${l.video ? '' : '&voz=1'}`);
        if (pestana) pestana.location.href = url; else window.location.href = url;
      } else {
        const p = leerPrefs();
        nav(`/${l.codigo}`, { state: { directo: true, video: l.video && p.cam !== false } });
      }
    } catch (e) {
      if (pestana) pestana.close();
      setLlamada(null);
      toast.error(textoError(e, 'La llamada ya terminó'));
    } finally {
      setOcupado(false);
    }
  }, [llamada, ocupado, enPortal, nav]);

  if (!llamada || (!enPortal && loc.pathname === `/${llamada.codigo}`)) return null;
  if (completa) {
    return (
      <div className="mt-entrante-completa" role="alertdialog" aria-live="assertive" aria-label={`${llamada.quien} te está llamando`} data-testid="meet-llamada-entrante">
        <span className="ec-marca">{llamada.video ? 'Videollamada entrante' : 'Llamada entrante'} · Orvian Meet</span>
        <b className="ec-nombre">{llamada.quien}</b>
        <span className="ec-tipo">te está llamando</span>
        <div className="ec-centro"><div className="mt-entrante-onda"><Avatar nombre={llamada.quien} clave={llamada.quien} tam={148} /></div></div>
        <Deslizador video={llamada.video} ocupado={ocupado} alContestar={() => responder(true)} alRechazar={() => responder(false)} />
      </div>
    );
  }
  return (
    <div className="mt-entrante" role="alertdialog" aria-live="assertive" aria-label={`${llamada.quien} te está llamando`} data-testid="meet-llamada-entrante">
      <div className="mt-entrante-onda"><Avatar nombre={llamada.quien} clave={llamada.quien} tam={56} /></div>
      <div className="mt-entrante-texto">
        <b>{llamada.quien}</b>
        <span>te está llamando</span>
        <small>{llamada.video ? 'Videollamada · Orvian Meet' : 'Llamada de voz · Orvian Meet'}</small>
      </div>
      <div className="mt-entrante-botones">
        <button type="button" className="mt-entrante-btn is-rechazar" onClick={() => responder(false)} aria-label="Rechazar" title="Rechazar" data-testid="meet-llamada-rechazar"><PhoneOff size={22} /></button>
        <button type="button" className="mt-entrante-btn is-aceptar" onClick={() => responder(true)} disabled={ocupado} aria-label="Contestar" title="Contestar" data-testid="meet-llamada-aceptar">
          {llamada.video ? <Video size={22} /> : <Phone size={22} />}
        </button>
      </div>
    </div>
  );
}
